/**
 * Admin Blog Service
 * 
 * Service for admin blog management operations (create, update, delete).
 * Requires authentication.
 */

import { apiGet, apiPost, apiPut, apiDelete } from './apiClient';
import { apiEndpoints } from '../config/environment';
import type {
  BlogPostInput,
  AdminBlogPostResponse,
  AdminBlogListResponse,
  DeleteResponse,
} from '../types/admin.types';
import type { BlogPost } from '../types/blog.types';
import type { ApiResponse } from '../types/api.types';

/**
 * Note: Authentication is now handled by Cloudflare Access at the edge.
 * No auth headers are needed - Cloudflare Access protects /api/admin/* routes.
 */

/**
 * Admin Blog Service API
 */
export const adminBlogService = {
  /**
   * Create a new blog post
   */
  createBlog: async (blogData: BlogPostInput): Promise<BlogPost> => {
    const endpoint = apiEndpoints.admin.blogs.create;
    const response = await apiPost<AdminBlogPostResponse>(
      endpoint,
      blogData
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to create blog post');
  },

  /**
   * Update an existing blog post
   */
  updateBlog: async (
    id: string,
    blogData: Partial<BlogPostInput>
  ): Promise<BlogPost> => {
    const endpoint = apiEndpoints.admin.blogs.update(id);
    const response = await apiPut<AdminBlogPostResponse>(
      endpoint,
      blogData
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to update blog post');
  },

  /**
   * Delete a blog post
   */
  deleteBlog: async (id: string): Promise<void> => {
    const endpoint = apiEndpoints.admin.blogs.delete(id);
    const response = await apiDelete<ApiResponse<DeleteResponse>>(
      endpoint
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete blog post');
    }
  },

  /**
   * Get a blog post for editing (includes raw markdown)
   */
  getBlogForEdit: async (id: string): Promise<BlogPost & { rawContent?: string }> => {
    const endpoint = apiEndpoints.admin.blogs.getById(id);
    const response = await apiGet<AdminBlogPostResponse>(
      endpoint
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to fetch blog post');
  },

  /**
   * Get list of all blog posts (including drafts)
   */
  getBlogList: async (options?: {
    includeDrafts?: boolean;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<BlogPost[]> => {
    const endpoint = apiEndpoints.admin.blogs.list;
    const queryParams = new URLSearchParams();
    
    if (options?.includeDrafts !== false) {
      queryParams.append('includeDrafts', 'true');
    }
    if (options?.page) {
      queryParams.append('page', options.page.toString());
    }
    if (options?.limit) {
      queryParams.append('limit', options.limit.toString());
    }
    if (options?.search) {
      queryParams.append('search', options.search);
    }
    
    const queryString = queryParams.toString();
    const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    const response = await apiGet<AdminBlogListResponse>(
      fullEndpoint
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to fetch blog list');
  },

  /**
   * Publish a blog post
   */
  publishBlog: async (id: string): Promise<BlogPost> => {
    const endpoint = apiEndpoints.admin.blogs.publish(id);
    const response = await apiPost<AdminBlogPostResponse>(
      endpoint,
      {}
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to publish blog post');
  },
};

